import MuiButton from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React, { useState } from 'react';

import { ActionPermissions } from '../../../../../@types';
import { PrintLabelEntityTypes } from '../../../../../@types/print-label.type';
import { PrintLabel } from '../../../../../components/molecules/PintLabel';
import { accentBtnSx, dangerBtnSx, outlinedBtnSx } from '../../../../../configs/detailPanelStyles';
import { isUserHasPermission } from '../../../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';

export interface FreightOrdersData {
  mainId?: number;
  mainOrderId?: string;
  mainOrderNumber?: string;
  mainOrderShowArchived?: boolean;
  returnedId?: number;
  returnedOrderId?: string;
  returnedOrderNumber?: string;
  returnedOrderShowArchived?: boolean;
}

export interface FreightOrderActionsProps {
  ordersData: FreightOrdersData;
  hasSelectedPackages: boolean;
  toastMessageKey?: string;
  onSaveOrder: () => void;
  onArchiveOrder: () => void;
  onArchiveReturnOrder: () => void;
  onReturnOrder: () => void;
  onQuote: () => void;
}

/**
 * The freight order's actions, in one row at the top right of the panel. They
 * sat at the foot until toasts — which land bottom-right — started covering
 * Save and Quote. They used to be pushed across by a hardcoded `ml={18}` and
 * coloured four different ways; now one accent action leads and the rest are
 * outlined, so the row reads as one set.
 */
export const FreightOrderActions: React.FC<FreightOrderActionsProps> = ({
  ordersData,
  hasSelectedPackages,
  toastMessageKey,
  onSaveOrder,
  onArchiveOrder,
  onArchiveReturnOrder,
  onReturnOrder,
  onQuote,
}) => {
  const { data: permissions } = useGetUserSystemPermissions();
  const canDispatch = isUserHasPermission(ActionPermissions.Shipment_View_Dispatch, permissions);
  const isOrdered = Boolean(ordersData.mainOrderShowArchived);

  // These used to be the shared `Button` atom, which held itself disabled while
  // its handler was in flight. Plain MUI buttons do not, and a freight order is
  // exactly the thing you do not want submitted twice — so the row keeps that
  // behaviour itself, disabling every action until the invoked one settles.
  const [pending, setPending] = useState(false);
  const guard = (action: () => void | Promise<void>) => async () => {
    if (pending) return;
    setPending(true);
    try {
      await action();
    } finally {
      setPending(false);
    }
  };

  return (
    <Stack
      key={toastMessageKey}
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={0.75}
      flexWrap="wrap"
      rowGap={0.75}
    >
      {isOrdered && ordersData.mainId !== undefined && (
        <PrintLabel
          entityId={ordersData.mainId}
          entityType={PrintLabelEntityTypes.FreightOrderLabel}
          buttonLabel="Print Connote"
        />
      )}
      {ordersData.returnedOrderShowArchived && ordersData.returnedId !== undefined && (
        <PrintLabel
          entityId={ordersData.returnedId}
          entityType={PrintLabelEntityTypes.FreightOrderLabel}
          buttonLabel="Print Return Connote"
        />
      )}

      <MuiButton
        variant="outlined"
        size="small"
        sx={outlinedBtnSx}
        disabled={pending || !canDispatch || !hasSelectedPackages}
        onClick={guard(onQuote)}
      >
        Quote
      </MuiButton>

      {ordersData.returnedOrderShowArchived && (
        <MuiButton
          variant="outlined"
          size="small"
          sx={dangerBtnSx}
          disabled={pending || !canDispatch}
          onClick={guard(onArchiveReturnOrder)}
        >
          Archive return order
        </MuiButton>
      )}
      {!ordersData.returnedOrderShowArchived && isOrdered && (
        <MuiButton
          variant="outlined"
          size="small"
          sx={outlinedBtnSx}
          disabled={pending || !canDispatch}
          onClick={guard(onReturnOrder)}
        >
          Return order
        </MuiButton>
      )}

      {isOrdered ? (
        <MuiButton
          variant="outlined"
          size="small"
          sx={dangerBtnSx}
          disabled={pending || !canDispatch}
          onClick={guard(onArchiveOrder)}
        >
          Archive order
        </MuiButton>
      ) : (
        <MuiButton
          variant="contained"
          size="small"
          sx={accentBtnSx}
          disabled={pending || !canDispatch}
          onClick={guard(onSaveOrder)}
        >
          Save
        </MuiButton>
      )}
    </Stack>
  );
};
